import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { isDevEnvironment } from '@/lib/env'

// Production ships fixed to French (the product targets the French market);
// English is the fallback layer for any missing key. A language switcher is
// exposed ONLY in dev-like environments (see LanguageSwitcher / lib/env), where
// the chosen language is persisted and also drives the API Accept-Language
// header so backend messages localize too.
export const DEFAULT_LANGUAGE = 'fr'
export const FALLBACK_LANGUAGE = 'en'

// Languages the switcher offers — the locales the frontend ships translations
// for. The backend additionally supports 'vi', but the FE has no 'vi' bundle.
export const SUPPORTED_LANGUAGES = ['fr', 'en'] as const
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_STORAGE_KEY = 'tigerflow-lang'

// Persisted choice is honoured only where switching is allowed; production
// always starts at DEFAULT_LANGUAGE regardless of any stale stored value.
function initialLanguage(): string {
  if (!isDevEnvironment) return DEFAULT_LANGUAGE
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  return stored && (SUPPORTED_LANGUAGES as readonly string[]).includes(stored)
    ? stored
    : DEFAULT_LANGUAGE
}

// Switch language + persist. The API client reads i18n.language per request,
// so subsequent calls carry the new Accept-Language automatically.
export function setAppLanguage(lng: AppLanguage): void {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lng)
  void i18n.changeLanguage(lng)
}

// Auto-load every locale namespace JSON so feature areas can add their own
// `locales/<lng>/<namespace>.json` files without editing a central index.
// Path shape: ./locales/<lng>/<namespace>.json
const modules = import.meta.glob<{ default: Record<string, unknown> }>(
  './locales/*/*.json',
  { eager: true }
)

const resources: Record<string, Record<string, Record<string, unknown>>> = {}

for (const path in modules) {
  const match = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path)
  if (!match) continue
  const [, lng, ns] = match
  resources[lng] ??= {}
  resources[lng][ns] = modules[path].default
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: FALLBACK_LANGUAGE,
  // Namespaces are discovered from the loaded files; `common` is the default.
  ns: Object.keys(resources[DEFAULT_LANGUAGE] ?? { common: {} }),
  defaultNS: 'common',
  fallbackNS: 'common',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
})

export default i18n
